import { useState, useEffect } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronLeft, ChevronRight, Users, ArrowLeft } from 'lucide-react';
import StudentCard from '@components/portfolio/StudentCard';
import Button from '@components/common/Button';
import portfolioService from '@services/portfolio.service';
import toast from 'react-hot-toast';
import { ROUTES } from '@constants';

const MyConnections = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const studentsPerPage = 12;

  useEffect(() => {
    fetchConnectedStudents();
  }, [currentPage]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchConnectedStudents();
      }
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  const fetchConnectedStudents = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: studentsPerPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await portfolioService.getConnectedStudents(params);
      
      if (response.success) {
        const responseData = response.data.data || response.data;
        setStudents(responseData.students || []);
        setTotalStudents(responseData.pagination?.total || 0);
        setTotalPages(responseData.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch connected students:', error);
      toast.error('Failed to load connected students');
      setStudents([]);
      setTotalStudents(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchConnectedStudents();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.HOME)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">My Connections</h1>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm md:text-base text-gray-600">
          {totalStudents} {totalStudents === 1 ? 'student' : 'students'} in your network (connections & followers)
        </p>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, skills, or field..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSearch}
            className="sm:w-auto"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </div>

        {searchTerm && (
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            className="text-sm"
          >
            Clear Search
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-5">
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm p-2 sm:p-3 md:p-5 animate-pulse"
            >
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 md:w-24 md:h-24 bg-gray-200 rounded-full mb-2 md:mb-4"></div>
                <div className="h-3 md:h-4 bg-gray-200 rounded w-16 sm:w-20 md:w-32 mb-2"></div>
                <div className="h-7 sm:h-8 md:h-10 bg-gray-200 rounded w-full mt-1 md:mt-3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : students.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-5 mb-4 md:mb-6">
            {students.map((student) => (
              <StudentCard key={student.id} student={student} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 md:gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 md:px-3 py-2"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="flex gap-1 md:gap-2">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-sm md:text-base ${
                          currentPage === pageNumber
                            ? 'bg-secondary-500 text-white'
                            : 'bg-white text-primary-700 hover:bg-secondary-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return (
                      <span
                        key={pageNumber}
                        className="px-2 py-2 text-primary-600"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-2"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-primary-700 mb-2">
            {searchTerm ? 'No Students Found' : 'No Connections Yet'}
          </h3>
          <p className="text-primary-600 mb-4">
            {searchTerm 
              ? 'Try adjusting your search' 
              : 'Start connecting with other students to see them here'}
          </p>
          {searchTerm ? (
            <Button
              variant="primary"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
            >
              Clear Search
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => navigate('/students')}
            >
              Browse Students
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MyConnections;
